import { InternalGameScreenshot } from '../../../api/screenshot-links'
import { InternalCategoryFeature, InternalCategoryFeatureChoice } from '../../../types/InternalCategory'
import { ActiveFeatures, AnswerItem, CategoryAnswers, InternalAnalysis } from '../types/InternalAnalysis'

// Legacy lists only the active choices.
export const toChoiceFeatures = (features: InternalCategoryFeature[]): InternalCategoryFeature[] =>
  features.map((feature) => ({ ...feature, choices: (feature.choices ?? []).filter(({ active }) => active) }))

// Answers to features that aren't listed are dropped, so a save leaves them out, as legacy.
export const getCategoryAnswers = (analysis: Pick<InternalAnalysis, 'answers'>, categoryId: string, features: InternalCategoryFeature[]): CategoryAnswers => {
  const stored = analysis.answers?.[categoryId] ?? {}
  const listed = new Set(features.map(({ id }) => id))
  return Object.fromEntries(Object.entries(stored).filter(([featureId]) => listed.has(featureId)))
}

export const toAnswerItems = (answers: CategoryAnswers): AnswerItem[] => Object.entries(answers).map(([featureId, choiceId]) => ({ featureId, choiceId }))

export const setAnswer = (answers: CategoryAnswers, featureId: string, weightId?: number): CategoryAnswers => {
  const { [featureId]: _removed, ...rest } = answers
  return weightId === undefined ? rest : { ...answers, [featureId]: weightId }
}

// Clicking the selected toggle clears it.
export const toggleAnswer = (answers: CategoryAnswers, featureId: string, weightId: number) =>
  setAnswer(answers, featureId, answers[featureId] === weightId ? undefined : weightId)

export const sameAnswers = (a: CategoryAnswers, b: CategoryAnswers) => {
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key])
}

// Legacy's page progress counts against the listed features, not the category's feature count.
export const getChoicesProgress = (answers: CategoryAnswers, featureCount: number) => {
  const count = Object.keys(answers).length
  if (!count) return 0
  return count > featureCount ? 100 : Math.ceil((count / featureCount) * 100)
}

// A benchmark answer counts only as one of the feature's active choices.
export const getBenchmarkChoice = (
  feature: InternalCategoryFeature,
  analysis: Pick<InternalAnalysis, 'answers'> | null | undefined
): InternalCategoryFeatureChoice | undefined => {
  const weightId = analysis?.answers?.[feature.categoryId]?.[feature.id]
  return weightId === undefined ? undefined : feature.choices.find((choice) => choice.weightId === weightId)
}

// Legacy highlights an answered feature when any benchmark answers it differently or not at all.
export const differsFromBenchmarks = (
  feature: InternalCategoryFeature,
  answers: CategoryAnswers,
  benchmarkAnalyses: (Pick<InternalAnalysis, 'answers'> | null | undefined)[]
) => {
  const answer = answers[feature.id]
  if (answer === undefined || !benchmarkAnalyses.length) return false
  return benchmarkAnalyses.some((analysis) => getBenchmarkChoice(feature, analysis)?.weightId !== answer)
}

// A feature that wasn't active for the analysed version.
export const isFutureFeature = (feature: InternalCategoryFeature, activeFeatures?: ActiveFeatures) =>
  !!activeFeatures && !(activeFeatures[feature.categoryId] ?? []).includes(feature.id)

export const getFeatureScreenshots = (screenshots: InternalGameScreenshot[] | undefined, legacyId: number) =>
  (screenshots ?? []).filter(({ features }) => features?.[legacyId] !== undefined && features?.[legacyId] !== null)
